import { initializeApp, getApps, getApp } from "firebase/app";
import { getFirestore, doc, getDocFromServer } from "firebase/firestore";
import { getAuth } from "firebase/auth";

const win = typeof window !== "undefined" ? (window as any) : {};

// Environment-driven setup: use window.FIREBASE_CONFIG injected by server or fall back to environment variables
const firebaseConfig = win.FIREBASE_CONFIG || {
  apiKey: (import.meta as any).env?.VITE_FIREBASE_API_KEY || "",
  projectId: (import.meta as any).env?.VITE_FIREBASE_PROJECT_ID || "",
  appId: (import.meta as any).env?.VITE_FIREBASE_APP_ID || "",
  authDomain: (import.meta as any).env?.VITE_FIREBASE_AUTH_DOMAIN || "",
  firestoreDatabaseId: (import.meta as any).env?.VITE_FIREBASE_FIRESTORE_DATABASE_ID || "(default)",
  storageBucket: (import.meta as any).env?.VITE_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: (import.meta as any).env?.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
};

// Validate loaded configurations
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.warn(
    "Firebase configuration values are missing in environment. Set VITE_FIREBASE_API_KEY and VITE_FIREBASE_PROJECT_ID."
  );
}

// Lazy initialization pattern with safe try-catch wrapper to prevent white screen crashes
let app: any = {};
let db: any = {};
let auth: any = {
  onAuthStateChanged: (_cb: any) => () => {},
  currentUser: null,
};
let isFirebaseConfigured = false;

function configureFirebase(config: any): boolean {
  if (!config || !config.apiKey || !config.apiKey.startsWith("AIza")) {
    return false;
  }
  try {
    app = getApps().length === 0 ? initializeApp(config) : getApp();
    db = getFirestore(app, config.firestoreDatabaseId || "(default)");
    auth = getAuth(app);
    isFirebaseConfigured = true;

    // Gracefully handle auth token refresh failures during offline/intermittent network
    if (auth && typeof auth.onIdTokenChanged === "function") {
      auth.onIdTokenChanged(async (user: any) => {
        if (user) {
          try {
            await user.getIdToken();
          } catch (err: any) {
            const msg = err?.message || String(err);
            const errCode = err?.code || "";
            if (errCode === "auth/network-request-failed" || msg.includes("network-request-failed") || msg.includes("Fetching auth token failed")) {
              console.warn("[Firebase Auth] Network request failed while refreshing token; client will operate in offline cache mode.");
            } else if (
              errCode === "auth/user-token-expired" ||
              errCode === "auth/invalid-user-token" ||
              msg.includes("TOKEN_EXPIRED") ||
              msg.includes("USER_NOT_FOUND")
            ) {
              console.warn("[Firebase Auth] Stale user session detected; signing out gracefully.");
              try {
                await auth.signOut();
              } catch (_) {}
            }
          }
        }
      });
    }

    // Validate connection to Firestore as specified in firebase-integration guidelines
    if (typeof window !== "undefined") {
      setTimeout(async () => {
        try {
          await getDocFromServer(doc(db, "test", "connection"));
        } catch (err: any) {
          const errMsg = err?.message || String(err);
          if (
            errMsg.includes("the client is offline") ||
            errMsg.includes("network-request-failed") ||
            errMsg.includes("Could not reach Cloud Firestore") ||
            errMsg.includes("unavailable")
          ) {
            console.warn("[Firestore] Currently operating in offline persistence mode.");
          }
        }
      }, 100);
    }
    return true;
  } catch (error: any) {
    console.error("Firebase Initialization Failure:", error.message || error);
    isFirebaseConfigured = false;
    return false;
  }
}

// Initial synchronous attempt (e.g. if build-time env vars or window object are present)
const initialConfig = win.FIREBASE_CONFIG || {
  apiKey: (import.meta as any).env?.VITE_FIREBASE_API_KEY || "",
  projectId: (import.meta as any).env?.VITE_FIREBASE_PROJECT_ID || "",
  appId: (import.meta as any).env?.VITE_FIREBASE_APP_ID || "",
  authDomain: (import.meta as any).env?.VITE_FIREBASE_AUTH_DOMAIN || "",
  firestoreDatabaseId: (import.meta as any).env?.VITE_FIREBASE_FIRESTORE_DATABASE_ID || "(default)",
  storageBucket: (import.meta as any).env?.VITE_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: (import.meta as any).env?.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
};

if (initialConfig.apiKey && initialConfig.apiKey.startsWith("AIza")) {
  configureFirebase(initialConfig);
}

// Asynchronous client bootstrapper for dynamic runtime loading
export async function initFirebaseClient(): Promise<boolean> {
  if (isFirebaseConfigured && app && db && typeof db.type === "string") {
    return true;
  }

  if (typeof window !== "undefined") {
    try {
      const res = await fetch("/api/bootstrap/config");
      if (res.ok) {
        const data = await res.json();
        if (data.googleMapsApiKey) {
          (window as any).GOOGLE_MAPS_API_KEY = data.googleMapsApiKey;
        }
        if (data.firebase && data.firebase.apiKey) {
          (window as any).FIREBASE_CONFIG = data.firebase;
          return configureFirebase(data.firebase);
        }
      }
    } catch (e) {
      console.warn("[Firebase] Bootstrap config fetch failed:", e);
    }
  }

  return isFirebaseConfigured;
}

export { app, db, auth, isFirebaseConfigured, configureFirebase };

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  };
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const message = error instanceof Error ? error.message : String(error);
  const code = (error as any)?.code;

  const isQuotaExceeded =
    code === 'resource-exhausted' ||
    message.includes('Quota limit exceeded') ||
    message.includes('resource-exhausted') ||
    message.includes('Free daily write units') ||
    message.includes('Free daily read units');

  const isUnavailable =
    code === 'unavailable' ||
    code === 'auth/network-request-failed' ||
    message.includes('unavailable') ||
    message.includes('Could not reach Cloud Firestore') ||
    message.includes('the client is offline') ||
    message.includes('Fetching auth token failed') ||
    message.includes('network-request-failed') ||
    message.includes('Failed to get document because the client is offline') ||
    message.includes('Connection failed');

  const isPermissionDenied =
    code === 'permission-denied' ||
    message.includes('Missing or insufficient permissions') ||
    message.includes('permission-denied');

  const errInfo: FirestoreErrorInfo = {
    error: message,
    authInfo: {
      userId: auth.currentUser?.uid || null,
      email: auth.currentUser?.email || null,
      emailVerified: auth.currentUser?.emailVerified || null,
      isAnonymous: auth.currentUser?.isAnonymous || null,
      tenantId: auth.currentUser?.tenantId || null,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };

  if (isQuotaExceeded) {
    console.warn(`[Firestore Quota Exceeded] (${operationType} at ${path}): Daily quota limit reached on the free tier. Operating in fallback mode.`, message);
    return;
  }

  if (isUnavailable) {
    console.warn(`[Firestore Connection Unavailable] (${operationType} at ${path}): Operating in offline mode.`);
    return;
  }

  console.warn(`Firestore Error (${operationType} at ${path}) [${code || 'no-code'}]:`, message);

  if (isPermissionDenied) {
    console.warn(`[Firestore Permission Notice] (${operationType} at ${path}):`, JSON.stringify(errInfo));
  }
  return errInfo;
}

/**
 * Strips all undefined fields recursively so Firestore setDoc/updateDoc/addDoc
 * never throws "Unsupported field value: undefined".
 */
export function cleanFirestorePayload<T>(data: T): T {
  if (data === undefined || data === null) return data;
  return JSON.parse(
    JSON.stringify(data, (_, value) => (value === undefined ? undefined : value))
  );
}

/**
 * Returns authorization headers with the current user's Firebase Auth ID token
 * for authenticating backend administrative API requests.
 */
export async function getAuthHeader(): Promise<Record<string, string>> {
  try {
    if (auth && typeof auth.currentUser?.getIdToken === "function") {
      const token = await auth.currentUser.getIdToken();
      if (token) {
        return { Authorization: `Bearer ${token}` };
      }
    }
  } catch (e) {
    console.debug("[Auth] Token retrieval skipped:", e);
  }
  return {};
}




